// AssumptionsDrawer.js — Editable assumptions with audit trail.
// Fields for each assumption parameter, audit log table, save button.

import React, { useState, useMemo, useCallback } from 'react';
import { Settings, Save, History } from 'lucide-react';

const ASSUMPTION_FIELDS = [
  { key: 'swift_fee_chf', label: 'SWIFT Fee (CHF)', type: 'number', step: '0.01', hint: 'Per-transfer SWIFT cost' },
  { key: 'fx_spread_pct', label: 'FX Spread (%)', type: 'number', step: '0.001', hint: 'Bid-ask spread on FX conversion' },
  { key: 'wht_rate_pct', label: 'WHT Rate (%)', type: 'number', step: '0.01', hint: 'Withholding tax rate on T-bill interest' },
  { key: 'forward_premium_annualised_pct', label: 'Forward Premium (ann. %)', type: 'number', step: '0.01', hint: 'Annualised forward hedge premium' },
  { key: 'custody_fee_pct', label: 'Custody Fee (%)', type: 'number', step: '0.001', hint: 'Annual custody/safekeeping fee' },
  { key: 'reinvestment_rate_pct', label: 'Reinvestment Rate (%)', type: 'number', step: '0.01', hint: 'Assumed reinvestment rate for coupon proceeds' },
  { key: 'funding_cost_annualised_pct', label: 'Funding Cost (ann. %)', type: 'number', step: '0.01', hint: 'Forwardsflow internal funding cost' },
  { key: 'operational_cost_monthly_kes', label: 'Operational Cost (monthly KES)', type: 'number', step: '100', hint: 'Monthly operational overhead allocation' },
];

function fmtDate(dateStr) {
  if (!dateStr) return '--';
  const d = new Date(dateStr);
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

export default function AssumptionsDrawer({ assumptions, onSave, auditLog = [] }) {
  // Local editable state, seeded from props
  const [values, setValues] = useState(() => {
    const initial = {};
    ASSUMPTION_FIELDS.forEach(f => {
      initial[f.key] = assumptions?.[f.key] ?? '';
    });
    return initial;
  });
  const [saving, setSaving] = useState(false);
  const [showAudit, setShowAudit] = useState(false);

  // Detect whether values have changed from original assumptions
  const hasChanges = useMemo(() => {
    return ASSUMPTION_FIELDS.some(f => {
      const original = assumptions?.[f.key] ?? '';
      const current = values[f.key] ?? '';
      return String(original) !== String(current);
    });
  }, [values, assumptions]);

  const handleChange = useCallback((key, rawValue) => {
    setValues(prev => ({ ...prev, [key]: rawValue }));
  }, []);

  const handleSave = useCallback(async () => {
    if (!onSave || !hasChanges) return;
    setSaving(true);
    try {
      // Build payload with only changed fields
      const payload = {};
      ASSUMPTION_FIELDS.forEach(f => {
        const val = values[f.key];
        if (val !== '' && val != null) {
          payload[f.key] = parseFloat(val);
        }
      });
      await onSave(payload);
    } finally {
      setSaving(false);
    }
  }, [values, onSave, hasChanges]);

  return (
    <div className="bg-white border border-zinc-200 rounded-none p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Settings className="w-4 h-4" style={{ color: '#4D4D4D' }} />
          <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
            Assumptions
          </span>
        </div>
        <button
          onClick={() => setShowAudit(!showAudit)}
          className="flex items-center gap-1.5 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.15em] border border-zinc-200 hover:bg-zinc-50 transition-colors"
          style={{ color: '#4D4D4D' }}
        >
          <History className="w-3 h-3" />
          {showAudit ? 'Hide Audit' : 'Audit Trail'}
          {auditLog.length > 0 && (
            <span className="ml-1 px-1 py-0.5 bg-zinc-100 text-zinc-600 text-[9px]">
              {auditLog.length}
            </span>
          )}
        </button>
      </div>

      {/* Editable fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        {ASSUMPTION_FIELDS.map(field => (
          <div key={field.key}>
            <label
              htmlFor={`assumption-${field.key}`}
              className="block font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1"
            >
              {field.label}
            </label>
            <input
              id={`assumption-${field.key}`}
              type={field.type}
              step={field.step}
              value={values[field.key]}
              onChange={e => handleChange(field.key, e.target.value)}
              className="w-full px-2 py-1.5 font-mono text-xs tabular-nums border border-zinc-200 bg-white text-zinc-900 focus:outline-none focus:border-zinc-400 transition-colors"
              placeholder={field.hint}
            />
            <p className="font-mono text-[9px] text-zinc-400 mt-0.5">{field.hint}</p>
          </div>
        ))}
      </div>

      {/* Save button */}
      <div className="flex items-center gap-3 border-t border-zinc-200 pt-3">
        <button
          onClick={handleSave}
          disabled={!hasChanges || saving}
          className="flex items-center gap-1.5 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ backgroundColor: hasChanges && !saving ? '#4D4D4D' : '#a1a1aa' }}
        >
          <Save className="w-3.5 h-3.5" />
          {saving ? 'Saving...' : 'Save Assumptions'}
        </button>
        {hasChanges && (
          <span className="font-mono text-[10px] text-amber-600">
            Unsaved changes
          </span>
        )}
      </div>

      {/* Audit trail */}
      {showAudit && (
        <div className="mt-4 border-t border-zinc-200 pt-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-2">
            Change History
          </p>
          {auditLog.length === 0 ? (
            <p className="font-mono text-xs text-zinc-400">
              No changes recorded yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <th className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 text-left py-2 px-2 font-medium">
                      Date
                    </th>
                    <th className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 text-left py-2 px-2 font-medium">
                      Field
                    </th>
                    <th className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 text-right py-2 px-2 font-medium">
                      Old Value
                    </th>
                    <th className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 text-right py-2 px-2 font-medium">
                      New Value
                    </th>
                    <th className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 text-left py-2 px-2 font-medium">
                      User
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {auditLog.map((entry, i) => (
                    <tr key={entry.id || i} className="border-b border-zinc-100">
                      <td className="py-1.5 px-2 font-mono tabular-nums text-zinc-700 whitespace-nowrap">
                        {fmtDate(entry.changed_at)}
                      </td>
                      <td className="py-1.5 px-2 font-mono text-zinc-700">
                        {entry.field_name || '--'}
                      </td>
                      <td className="py-1.5 px-2 text-right font-mono tabular-nums text-zinc-500">
                        {entry.old_value ?? '--'}
                      </td>
                      <td className="py-1.5 px-2 text-right font-mono tabular-nums text-zinc-900">
                        {entry.new_value ?? '--'}
                      </td>
                      <td className="py-1.5 px-2 font-mono text-zinc-500 text-xs">
                        {entry.changed_by || '--'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
